import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "./api";
import { LIVE_OFF_AFTER_MS, reconnectDelay } from "./live-policy";
import type { AgentMessage } from "./types";

/** What `/api/ws` pushes. Mirrors `BoardEvent` in packages/queue. */
export type LiveEvent =
  | { type: "hello"; userId: string }
  /** `revision`: the counter after the commit; `tables`: what it wrote (`"*"`: could not tell). Both may be absent. */
  | { type: "revision"; revision?: number; tables?: string[] }
  | { type: "agent.message"; agentId: string; message: AgentMessage }
  | { type: "agent.updated"; agentId: string }
  | { type: "agent.delta"; agentId: string; turnId: string; text: string }
  | { type: "agent.delta.reset"; agentId: string; turnId: string };

type Listener = (event: LiveEvent) => void;

interface Live {
  connected: boolean;
  off: boolean;
  subscribe: (listener: Listener) => () => void;
}

const LiveContext = createContext<Live>({ connected: false, off: false, subscribe: () => () => undefined });

/**
 * One websocket for the whole app, reconnecting with backoff. It is a
 * notification channel only: every view still loads its data over REST, and the
 * revision poll keeps working while this is down, so a dropped socket costs
 * latency and nothing else.
 */
export function LiveProvider({ children }: { children: ReactNode }) {
  const listeners = useRef(new Set<Listener>());
  const [connected, setConnected] = useState(false);
  const [off, setOff] = useState(false);

  useEffect(() => {
    let socket: WebSocket | null = null;
    let retry = 0;
    let timer: ReturnType<typeof setTimeout> | undefined;
    let offTimer: ReturnType<typeof setTimeout> | undefined;
    let closed = false;

    // Down (or never up) for a while: say so, once, until it is back.
    const armOff = () => {
      if (offTimer === undefined) offTimer = setTimeout(() => setOff(true), LIVE_OFF_AFTER_MS);
    };

    const connect = () => {
      const scheme = window.location.protocol === "https:" ? "wss" : "ws";
      socket = new WebSocket(`${scheme}://${window.location.host}/api/ws`);
      socket.onopen = () => {
        retry = 0;
        clearTimeout(offTimer);
        offTimer = undefined;
        setOff(false);
        setConnected(true);
      };
      socket.onmessage = (message) => {
        let event: LiveEvent;
        try {
          event = JSON.parse(String(message.data)) as LiveEvent;
        } catch {
          return;
        }
        for (const listener of listeners.current) listener(event);
      };
      socket.onclose = (event) => {
        setConnected(false);
        if (closed) return;
        armOff();
        retry += 1;
        const delay = reconnectDelay(event.code, retry);
        if (delay === null) {
          // The server refused the session. Ask REST: a 401 there signs the user out
          // the usual way (api.ts); if REST still accepts it, the revision poll
          // carries on alone rather than hammering a socket that will refuse again.
          void api.revision().catch(() => undefined);
          return;
        }
        timer = setTimeout(connect, delay);
      };
    };
    armOff();
    connect();
    return () => {
      closed = true;
      clearTimeout(timer);
      clearTimeout(offTimer);
      socket?.close();
    };
  }, []);

  const value = useRef<Live>({
    connected: false,
    off: false,
    subscribe: (listener) => {
      listeners.current.add(listener);
      return () => listeners.current.delete(listener);
    },
  });
  return <LiveContext.Provider value={{ ...value.current, connected, off }}>{children}</LiveContext.Provider>;
}

export const useLiveConnected = (): boolean => useContext(LiveContext).connected;

/** The socket has been down for `LIVE_OFF_AFTER_MS`: changes arrive by the poll, a few seconds late. */
export const useLiveOff = (): boolean => useContext(LiveContext).off;

export const LIVE_OFF_LABEL = "Live updates off — changes still arrive every few seconds";

/** Calls `handler` for every live event. The latest handler is used without resubscribing. */
export function useLiveEvent(handler: Listener): void {
  const { subscribe } = useContext(LiveContext);
  const latest = useRef(handler);
  latest.current = handler;
  useEffect(() => subscribe((event) => latest.current(event)), [subscribe]);
}
